import { List, Map, fromJS } from 'immutable'
import { EUR, renderSymbol } from 'services/currency'
import { format, formatUTC, getDateTime } from 'services/date-time'
import get from 'lodash/get'
import {
  TYPE_BROOKLYN,
  get as apiGet,
} from 'api-client'

export const AMBASSADORS_START_YEAR = 2021
export const PAYOUT_MIN = 300
export const SIGNUPS_MAX_PP = 12

const date = new Date()
export const currentYear = date.getFullYear()
export const nextYear = new Date(new Date().setFullYear(currentYear + 1)).getFullYear()
export const CURRENT_YEAR_START = format(new Date(`${currentYear}-01-01T14:00:00`), 'YYYY-MM-DD')
export const CURRENT_YEAR_END = format(new Date(`${currentYear}-12-31T14:00:00`), 'YYYY-MM-DD')
export const NEXT_YEAR_END = format(new Date(`${nextYear}-12-31T14:00:00`), 'YYYY-MM-DD')

export const AMBASSADORS_PAGE_STATIC_KEY = 'ambassadorsPage'

export const PARTNER_DETAIL_TAB_VALUE = 'partner-details'
export const PARTNER_PAYOUTS_TAB_VALUE = 'partner-payouts'
export const PARTNER_SIGNUPS_TAB_VALUE = 'partner-signups'
export const PARTNER_CAMPAIGNS_TAB_VALUE = 'partner-campaigns'

// Campaigns tab field names
export const CAMPAIGN_FIELD_NAME = 'campaignName'
export const CAMPAIGN_FIELD_CLICKS = 'clicks'
export const CAMPAIGN_FIELD_LEADS = 'leads'
export const CAMPAIGN_FIELD_SIGNUPS = 'signups'
export const CAMPAIGN_FIELD_BILLINGS = 'payments'
export const CAMPAIGN_FIELD_EARNINGS_PER_CLICK = 'earningsPerClick'
export const CAMPAIGN_FIELD_EARNINGS_PER_LEADS = 'earningsPerLead'
export const CAMPAIGN_FIELD_CLICK_TO_LEAD = 'leadConversionRate'
export const CAMPAIGN_FIELD_LEAD_TO_SIGNUP = 'signupConversionRate'
export const CAMPAIGN_FIELD_EARNINGS = 'earnings'

// Signups tab field names
export const SIGNUPS_FIELD_CREATED = 'createdAt'
export const SIGNUPS_FIELD_PLAN = 'planName'
export const SIGNUPS_FIELD_EVENT = 'triggerEvent'
export const SIGNUPS_FIELD_CAMPAIGN = 'campaign'
export const SIGNUPS_FIELD_SOURCE = 'source'
export const SIGNUPS_FIELD_ID = 'id'
export const SIGNUPS_FIELD_MEDIUM = 'medium'

// Payouts tab field names
export const PAYOUTS_FIELD_MONTH = 'month'
export const PAYOUTS_FIELD_END_DATE = 'endDate'
export const PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS = 'unpaidFromPreviousMonthUSD'
export const PAYOUTS_FIELD_MONTHLY_EARNINGS = 'currentMonthCommissionUSD'
export const PAYOUTS_FIELD_TOTAL_EARNED = 'totalPayoutUSD'
export const PAYOUTS_FIELD_TOTAL_UNPAID = 'totalUnpaidUSD'
export const PAYOUTS_FIELD_ACTIVE = 'ambassadorIsActive'

/**
 * Format the correct currency value for the Ambassadors page
 * @param {number} amount
 * @param {string} currencyIso
 * @returns {string}
 */
export function formatAmount (options) {
  const {
    amount, currencyIso = 'USD', active = true, useSymbol = true,
  } = options
  const number = parseFloat(amount)
  if (Number.isNaN(Number(number)) || !active) {
    return '--'
  }

  let price = Math.abs(number).toFixed(2)
  price = String(price)
  if (useSymbol) {
    if (currencyIso === EUR) {
      price = `${price} ${renderSymbol(currencyIso)}`
    } else {
      price = `${renderSymbol(currencyIso)}${price}`
    }
  }

  return price
}

/**
 * Get an ambassador from brooklyn
 * @param {Object} options The options
 * @param {Object} options.auth auth
 */
export async function getAmbassador ({ auth }) {
  const response = await apiGet('/v1/ambassador', {}, { auth }, TYPE_BROOKLYN)

  return get(response, 'body', {})
}

/**
 * Get an ambassador from brooklyn
 * @param {Object} options The options
 * @param {Object} options.auth auth
 */
export async function getAmbassadorCampaigns ({ auth, year }) {
  const response = await apiGet('/v1/ambassador/campaigns/metrics', {
    minStartDate: year ? format(new Date(`${year}-01-01T14:00:00`), 'YYYY-MM-DD') : CURRENT_YEAR_START,
    maxStartDate: year ? format(new Date(`${year}-12-31T14:00:00`), 'YYYY-MM-DD') : CURRENT_YEAR_END,
  }, { auth }, TYPE_BROOKLYN)

  return get(response, 'body', [])
}

/**
 * Get an ambassador from brooklyn
 * @param {Object} options The options
 * @param {Object} options.auth auth
 */
export async function getAmbassadorAllCampaigns ({ auth }) {
  const response = await apiGet('/v1/ambassador/campaigns/metrics', {
    minStartDate: CURRENT_YEAR_START,
    maxStartDate: NEXT_YEAR_END,
  }, { auth }, TYPE_BROOKLYN)

  return get(response, 'body', [])
}

/**
 * Get past and upcoming Campaigns from current year campaings
 * @param {List} campaigns The options
 * @returns {Array} Cortege of past and upcoming Campaigns
 */
export const getPastUpcomingCampaigns = (campaigns = List()) => {
  let pastCampaigns = List()
  let upcomingCampaigns = List()

  campaigns.forEach((campaign) => {
    const campaignStartDate = campaign.get('campaignStartDate')

    if (getDateTime(campaignStartDate) > getDateTime(date)) {
      upcomingCampaigns = upcomingCampaigns.push(campaign)
    } else {
      pastCampaigns = pastCampaigns.push(campaign)
    }
  })

  return [pastCampaigns, upcomingCampaigns]
}

export function getSubHeaderCampaignsMetricsTableData (records = List(), firstColumn) {
  const subHeaderKeys = [
    CAMPAIGN_FIELD_CLICKS,
    CAMPAIGN_FIELD_LEADS,
    CAMPAIGN_FIELD_SIGNUPS,
    CAMPAIGN_FIELD_BILLINGS,
    CAMPAIGN_FIELD_EARNINGS,
    CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
    CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
    CAMPAIGN_FIELD_CLICK_TO_LEAD,
    CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
  ]

  let subHeader = Map({
    ...firstColumn,
  })

  records.forEach((record) => {
    subHeaderKeys.forEach((key) => {
      const keyValue = Number(subHeader.get(key, 0)) + Number(record.get(key, 0))

      subHeader = subHeader.set(key, keyValue)
    })
  })

  const totalClicks = subHeader.get(CAMPAIGN_FIELD_CLICKS)
  const totalLeads = subHeader.get(CAMPAIGN_FIELD_LEADS)
  const totalSignups = subHeader.get(CAMPAIGN_FIELD_SIGNUPS)
  const totalEarnings = subHeader.get(CAMPAIGN_FIELD_EARNINGS)

  subHeader = subHeader
    .set(
      CAMPAIGN_FIELD_CLICK_TO_LEAD,
      (totalLeads / totalClicks) * 100,
    )
    .set(
      CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
      (totalSignups / totalLeads) * 100,
    )
    .set(
      CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
      totalEarnings / totalClicks,
    )
    .set(
      CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
      totalEarnings / totalLeads,
    )

  return subHeader
}

/**
 * Get csv headers by columns
 * @param {Array} columns
 * @returns {Object}
 */
export const getCsvHeaders = (columns) => columns.reduce((acc, column) => {
  const { key, title } = column

  acc[key] = title

  return acc
}, {})

/**
 * Get Campaigns csv data
 * @param {Array} campaigns
 * @returns {Array}
 */
export const getCampaignsCsvData = (campaigns = List()) => campaigns.map((campaign) => [
  CAMPAIGN_FIELD_NAME,
  CAMPAIGN_FIELD_CLICKS,
  CAMPAIGN_FIELD_LEADS,
  CAMPAIGN_FIELD_SIGNUPS,
  CAMPAIGN_FIELD_BILLINGS,
  CAMPAIGN_FIELD_EARNINGS,
  CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
  CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
  CAMPAIGN_FIELD_CLICK_TO_LEAD,
  CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
].reduce((acc, key) => {
  acc[key] = campaign.get(key, '')

  return acc
}, {}))

/**
 * Get Signups csv data
 * @param {Array} signups
 * @returns {Array}
 */
export const getSignUpsCsvData = (signups = List(), language) => signups.map((signup) => [
  SIGNUPS_FIELD_CREATED,
  SIGNUPS_FIELD_PLAN,
  SIGNUPS_FIELD_EVENT,
  SIGNUPS_FIELD_CAMPAIGN,
  SIGNUPS_FIELD_SOURCE,
  SIGNUPS_FIELD_MEDIUM,
].reduce((acc, key) => {
  if (key === SIGNUPS_FIELD_CAMPAIGN) {
    acc[key] = signup.getIn([key, 'campaignName'], 'N/A') || 'N/A'
  } else if (key === SIGNUPS_FIELD_CREATED) {
    const createdDate = signup.get(key)
    acc[key] = createdDate ? formatUTC(createdDate, language).replace(',', '') : 'N/A'
  } else {
    acc[key] = signup.get(key, 'N/A') || 'N/A'
  }

  return acc
}, {}))

/**
 * Get Payouts csv data
 * @param {Array} payouts
 * @returns {Array}
 */
export const getPayoutsCsvData = (payouts = List()) => payouts.map((payout) => [
  PAYOUTS_FIELD_MONTH,
  PAYOUTS_FIELD_UNPAID_FROM_PREVIOUS,
  PAYOUTS_FIELD_MONTHLY_EARNINGS,
  PAYOUTS_FIELD_TOTAL_EARNED,
  PAYOUTS_FIELD_ACTIVE,
].reduce((acc, key) => {
  acc[key] = payout.get(key, '')

  return acc
}, {}))

/**
* Create the csv title
* @param {Map} locale The locale
* @param {String} prefix The prefix from static text
* @param {String} name The partner name
* @param {String} type The type from static text
* @param {String | undefined} month The month filter
* @param {String} year The year filter
* @returns {String} with csv title
*/
export const createCsvTitle = ({
  locale,
  prefix,
  name,
  type,
  month = false,
  downloaded,
  year,
}) => {
  const downloadedDate = format(new Date(), locale, 'YYYY-MM-DD')

  return `${prefix}_${name.replace(/\s+/g, '')}-${type}-${year}${month ? `-${month}` : ''}_${downloaded}-${downloadedDate}`
}

/**
 * Gets an ambassadors payout data
 * @param {Object} options the options
 * @param {Object} options.uuid the activity uuid
 * @param {Object} options.auth auth
 */
export async function getAmbassadorPayout ({ auth, year }) {
  const ambassadorUuid = auth.get('uuid')
  const response = await apiGet('/v1/ambassador/payouts', {
    ambassadorUuid,
    startDate: year ? format(new Date(`${year}-01-01T14:00:00`), 'YYYY-MM-DD') : CURRENT_YEAR_START,
    endDate: year ? format(new Date(`${year}-12-31T14:00:00`), 'YYYY-MM-DD') : CURRENT_YEAR_END,
  }, { auth }, TYPE_BROOKLYN)
  return get(response, 'body', {})
}

export const sortASC = (a, b) => {
  if (a < b) {
    return -1
  }

  if (a > b) {
    return 1
  }

  return 0
}

export const sortDESC = (a, b) => {
  if (a > b) {
    return -1
  }

  if (a < b) {
    return 1
  }

  return 0
}

export const orderData = (data, sortDirection, sortField) => {
  const comparator = sortDirection === 'ASC' ? sortASC : sortDESC
  const comparatorValueMapper = (item) => item.get(sortField)
  return fromJS(data).sortBy(comparatorValueMapper, comparator)
}

/**
 * Gets an ambassadors signup data
 * @param {Object} options the options
 * @param {Object} options.uuid the activity uuid
 * @param {Object} options.auth auth
 */
export async function getAmbassadorSignUp ({
  auth,
  year = currentYear,
  month = 0,
  p = 1,
  pp = 25,
}) {
  const ambassadorUuid = auth.get('uuid')
  const hasValidMonth = month > 0
  const numericMonth = hasValidMonth ? month : 1
  const selectedMonth = `${numericMonth}`.padStart(2, '0')
  const lastMonth = hasValidMonth ? selectedMonth : 12
  const lastDay = new Date(year, selectedMonth, 0).getDate()

  const response = await apiGet('/v1/ambassador/signups', {
    ambassadorUuid,
    startDate: format(new Date(`${year}-${selectedMonth}-01T14:00:00`), 'YYYY-MM-DD'),
    endDate: format(new Date(`${year}-${lastMonth}-${lastDay}T14:00:00`), 'YYYY-MM-DD'),
    p,
    pp,
  }, { auth }, TYPE_BROOKLYN)
  return get(response, 'body', {})
}

/**
 * Get an ambassador campaign detail from brooklyn
 * @param {Object} options The options
 * @param {Object} options.auth auth
 */
export async function getAmbassadorCampaignDetails ({ auth, utmCampaign }) {
  const response = await apiGet('/v1/ambassador/campaigns', {
    utmCampaign,
  }, { auth }, TYPE_BROOKLYN)

  return get(response, 'body', [])
}
